import React from 'react';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import _ from 'lodash';
import dateToDateTimeString from '@ssg/common/Helpers/dateToDateTimeString';
import { loader } from 'graphql.macro';
import { useQuery } from '@apollo/client';
import {
	GetCatalogCraftsmanChanges,
	GetCatalogCraftsmanChangesVariables,
	GetCatalogCraftsmanChanges_catalogCraftsman_changes_after,
	GetCatalogCraftsmanChanges_catalogCraftsman_changes_before,
} from '../../../GraphQL';
import Loading from '@ssg/common/Components/Loading';

const GET_CATALOG_CRAFTSMAN_CHANGES = loader('src/GraphQL/CatalogCraftsmen/GetCatalogCraftsmanChanges.gql');

interface IProps {
	craftsmanId: string;
	open: boolean;
	close: () => void;
}

const CraftsmanChangeLog: React.FC<IProps> = ({ craftsmanId, open, close }) => {
	const { t } = useTranslation();

	const { data, loading } = useQuery<GetCatalogCraftsmanChanges, GetCatalogCraftsmanChangesVariables>(GET_CATALOG_CRAFTSMAN_CHANGES, {
		variables: {
			id: craftsmanId,
		},
	});

	const sorted = React.useMemo(
		() => (data?.catalogCraftsman.changes ?? []).slice().sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()),
		[data?.catalogCraftsman.changes],
	);

	return (
		<>
			<Modal
				title={t('catalog.craftsman.craftsmanChanges')}
				size={ModalSize.MEDIUM}
				visible={open}
				close={close}
				body={
					<>
						{loading ? (
							<div className="relative h-28 w-full">
								<Loading />
							</div>
						) : (
							<div className="text-blue space-y-4">
								{sorted.map(c => (
									<div key={c.timestamp}>
										<strong>
											{dateToDateTimeString(c.timestamp)}
											&nbsp;{t('common.by')}&nbsp;
											{c.user.name}&nbsp;({c.user.email})
										</strong>

										<div className="mt-1">{renderChanges(t, c.before, c.after)}</div>
									</div>
								))}
							</div>
						)}
					</>
				}
			/>
		</>
	);
};

export default CraftsmanChangeLog;

function renderChanges(t: TFunction, before: GetCatalogCraftsmanChanges_catalogCraftsman_changes_before, after: GetCatalogCraftsmanChanges_catalogCraftsman_changes_after): React.ReactElement[] {
	const NO_VALUE = '\u00A0\u2014\u00A0';
	const result: React.ReactElement[] = [];

	// Type name was changed
	if (!(before.type === null && after.type === null) && before.type !== after.type) {
		const tValues = {
			before: before.type ?? NO_VALUE,
			after: after.type ?? NO_VALUE,
		};

		result.push(
			<div key="type" className="my-2">
				<Trans t={t} i18nKey="catalog.craftsman.changelog.type" values={tValues} components={[<mark />]} />
			</div>,
		);
	}

	// Contact name was changed
	if (!(before.contactName === null && after.contactName === null) && before.contactName !== after.contactName) {
		const tValues = {
			before: before.contactName ?? NO_VALUE,
			after: after.contactName ?? NO_VALUE,
		};

		result.push(
			<div key="contactName" className="my-2">
				<Trans t={t} i18nKey="catalog.contact.changelog.contactName" values={tValues} components={[<mark />]} />
			</div>,
		);
	}

	// Email was changed
	if (!(before.email === null && after.email === null) && before.email !== after.email) {
		const tValues = {
			before: before.email ?? NO_VALUE,
			after: after.email ?? NO_VALUE,
		};

		result.push(
			<div key="email" className="my-2">
				<Trans t={t} i18nKey="catalog.contact.changelog.email" values={tValues} components={[<mark />]} />
			</div>,
		);
	}

	// Global type was changed
	if (!(before.globalType === null && after.globalType === null) && before.globalType !== after.globalType) {
		const tValues = {
			before: t(`catalog.changelog.${before.globalType}`) ?? NO_VALUE,
			after: t(`catalog.changelog.${after.globalType}`) ?? NO_VALUE,
		};

		result.push(
			<div key="globalType" className="my-2">
				<Trans t={t} i18nKey="catalog.changelog.globalType" values={tValues} components={[<mark />]} />
			</div>,
		);
	}

	// Acitve was changed
	if (!(before.active === null && after.active === null) && before.active !== after.active) {
		const tValues = {
			before: before.active ? t('common.yes') : t('common.no'),
			after: after.active ? t('common.yes') : t('common.no'),
		};

		result.push(
			<div key="active" className="my-2">
				<Trans t={t} i18nKey="catalog.craftsman.changelog.active" values={tValues} components={[<mark />]} />
			</div>,
		);
	}

	// Priority was changed
	if (!(before.priority === null && after.priority === null) && before.priority !== after.priority) {
		const tValues = {
			before: before.priority ?? NO_VALUE,
			after: after.priority ?? NO_VALUE,
		};

		result.push(
			<div key="priority" className="my-2">
				<Trans t={t} i18nKey="catalog.craftsman.changelog.priority" values={tValues} components={[<mark />]} />
			</div>,
		);
	}

	// Informations was changed
	if (!(before.informations === null && after.informations === null)) {
		if (before.informations?.length !== after.informations?.length) {
			let beforeString = '';
			before.informations?.forEach(afInfo => {
				beforeString += `${afInfo?.phoneNumber}${afInfo.remark ? `, ${afInfo.remark}` : ''}, `;
			});
			let afterString = '';
			after.informations?.forEach(diff => {
				afterString += `${diff?.phoneNumber}${diff.remark ? `, ${diff.remark}` : ''}, `;
			});

			const tValues = {
				before: beforeString.trim().endsWith(',') ? beforeString.slice(0, beforeString.length - 2) : beforeString,
				after: afterString.trim().endsWith(',') ? afterString.slice(0, afterString.length - 2) : afterString,
			};

			result.push(
				<div key="phoneNumbers" className="my-2">
					<Trans t={t} i18nKey="catalog.craftsman.changelog.phoneNumbers" values={tValues} components={[<mark />]} />
				</div>,
			);
		} else if (_.difference(before.informations, after.informations ?? []).length !== 0) {
			let beforeString = '';
			before.informations?.forEach(afInfo => {
				beforeString += `${afInfo?.phoneNumber}${afInfo.remark ? `, ${afInfo.remark}` : ''}, `;
			});
			let afterString = '';
			after.informations?.forEach(diff => {
				afterString += `${diff?.phoneNumber}${diff.remark ? `, ${diff.remark}` : ''}, `;
			});

			const tValues = {
				before: beforeString.trim().endsWith(',') ? beforeString.slice(0, beforeString.length - 2) : beforeString,
				after: afterString.trim().endsWith(',') ? afterString.slice(0, afterString.length - 2) : afterString,
			};

			result.push(
				<div key="phoneNumbers" className="my-2">
					<Trans t={t} i18nKey="catalog.craftsman.changelog.phoneNumbers" values={tValues} components={[<mark />]} />
				</div>,
			);
		}
	}

	// Catalogs was changed
	if (!(before.catalogs === null && after.catalogs === null)) {
		if (before.catalogs?.length !== after.catalogs?.length) {
			let beforeString = '';
			before.catalogs?.forEach(afCat => {
				beforeString += `${afCat?.propertyNumber}, `;
			});
			let afterString = '';
			after.catalogs?.forEach(diff => {
				afterString += `${diff?.propertyNumber}, `;
			});

			const tValues = {
				before: beforeString.trim().endsWith(',') ? beforeString.slice(0, beforeString.length - 2) : beforeString,
				after: afterString.trim().endsWith(',') ? afterString.slice(0, afterString.length - 2) : afterString,
			};

			result.push(
				<div key="catalogs" className="my-2">
					<Trans t={t} i18nKey="catalog.timedMessage.changelog.catalogs" values={tValues} components={[<mark />]} />
				</div>,
			);
		} else if (_.difference(before.catalogs, after.catalogs ?? []).length !== 0) {
			let beforeString = '';
			before.catalogs?.forEach(afCat => {
				beforeString += `${afCat?.propertyNumber}, `;
			});
			let afterString = '';
			after.catalogs?.forEach(diff => {
				afterString += `${diff?.propertyNumber}, `;
			});

			const tValues = {
				before: beforeString.trim().endsWith(',') ? beforeString.slice(0, beforeString.length - 2) : beforeString,
				after: afterString.trim().endsWith(',') ? afterString.slice(0, afterString.length - 2) : afterString,
			};

			result.push(
				<div key="catalogs" className="my-2">
					<Trans t={t} i18nKey="catalog.timedMessage.changelog.catalogs" values={tValues} components={[<mark />]} />
				</div>,
			);
		}
	}

	return result;
}
